import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Trash, UserCircle } from "@phosphor-icons/react";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const changeRole = async (id, role) => {
    try {
      await api.put(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Admin</p>
        <h1 className="font-display text-4xl font-black tracking-tight">Registered users</h1>
        <p className="text-base font-medium text-slate-600 mt-1">
          Manage accounts. Standard users have view-only access; admins can manage everything.
        </p>
      </div>

      <div className="bg-white border-2 border-black rounded-xl brutal-shadow overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b-2 border-black bg-slate-50 text-xs font-bold uppercase tracking-widest">
          <div className="col-span-5">User</div>
          <div className="col-span-4">Role</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {users.map((u) => {
          const isSelf = u.id === user?.id;
          return (
            <div
              key={u.id}
              data-testid={`user-row-${u.id}`}
              className="grid grid-cols-12 gap-2 items-center px-5 py-4 border-b-2 border-black last:border-b-0"
            >
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <div
                  className={`w-10 h-10 rounded-lg border-2 border-black flex items-center justify-center ${
                    u.role === "admin" ? "bg-yellow-200" : "bg-emerald-200"
                  }`}
                >
                  {u.role === "admin" ? (
                    <ShieldCheck weight="bold" size={20} />
                  ) : (
                    <UserCircle weight="bold" size={20} />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-bold truncate">
                    {u.name} {isSelf && <span className="text-xs text-blue-600">(you)</span>}
                  </div>
                  <div className="text-xs font-medium text-slate-500 truncate">{u.email}</div>
                </div>
              </div>
              <div className="col-span-4">
                <select
                  data-testid={`user-role-${u.id}`}
                  value={u.role}
                  disabled={isSelf}
                  onChange={(e) => changeRole(u.id, e.target.value)}
                  className="border-2 border-black rounded-lg px-3 py-2 font-bold bg-white disabled:opacity-50"
                >
                  <option value="worker">Standard user</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="col-span-3 flex justify-end">
                <button
                  onClick={() => remove(u.id)}
                  disabled={isSelf}
                  data-testid={`user-delete-${u.id}`}
                  className="bg-red-300 border-2 border-black rounded-lg p-2 brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-40 disabled:hover:translate-x-0 disabled:hover:translate-y-0"
                >
                  <Trash weight="bold" size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
